import type { ScoreDoc, ScoreConflict } from '../types';

const DB_NAME = 'score-studio-local';
const DB_VERSION = 1;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains('scores')) db.createObjectStore('scores', { keyPath: 'id' });
      if (!db.objectStoreNames.contains('sync')) db.createObjectStore('sync', { keyPath: 'scoreId' });
      if (!db.objectStoreNames.contains('conflicts')) db.createObjectStore('conflicts', { keyPath: 'id' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function transact<T>(storeName: string, mode: IDBTransactionMode, action: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, mode);
    const request = action(transaction.objectStore(storeName));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    transaction.onabort = () => reject(transaction.error);
    transaction.oncomplete = () => db.close();
  });
}

export const getScore = (id: string) => transact<ScoreDoc | undefined>('scores', 'readonly', (store) => store.get(id));
export const listScores = () => transact<ScoreDoc[]>('scores', 'readonly', (store) => store.getAll());
export const putScore = (score: ScoreDoc) => transact<IDBValidKey>('scores', 'readwrite', (store) => store.put(score));
export const deleteScore = (id: string) => transact<undefined>('scores', 'readwrite', (store) => store.delete(id));
export const getSyncBase = (scoreId: string) => transact<ScoreDoc | undefined>('sync', 'readonly', (store) => store.get(scoreId));
export const putSyncBase = (score: ScoreDoc) => transact<IDBValidKey>('sync', 'readwrite', (store) => store.put({ scoreId: score.id, ...score }));
export const deleteSyncBase = (scoreId: string) => transact<undefined>('sync', 'readwrite', (store) => store.delete(scoreId));
export const getConflict = (id: string) => transact<ScoreConflict | undefined>('conflicts', 'readonly', (store) => store.get(id));
export const listConflicts = () => transact<ScoreConflict[]>('conflicts', 'readonly', (store) => store.getAll());
export const putConflict = (conflict: ScoreConflict) => transact<IDBValidKey>('conflicts', 'readwrite', (store) => store.put(conflict));
export const deleteConflict = (id: string) => transact<undefined>('conflicts', 'readwrite', (store) => store.delete(id));
